import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import buildMetricValuesHook from 'pared/Routes/renderer/utils/unifiedEndpoint/buildMetricValuesHook'
import { getBrandLocationGroupId } from 'pared/utils/brand'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IGuestSummaryNodeType {
  problemPercentage: number
  mostCommonDay: string[]
  mostCommonDaypart: string[]
  mostCommonProblem: string[]
}

interface IGuestSummaryType {
  locationGuestSummaryV2: {
    nodes: IGuestSummaryNodeType[]
  }
}

const query = gql`
  query LocationGuestSummaryV2(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationId: Int!
  ) {
    locationGuestSummaryV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iLocationId: $iLocationId
    ) {
      nodes {
        problemPercentage
        mostCommonDay
        mostCommonDaypart
        mostCommonProblem
      }
    }
  }
`

const useLocationSmgKpis = buildMetricValuesHook({
  groupFilterType: 'store',
  metrics: {
    codes: ['smg_training_completion_percent'],
  },
})

const useSystemwideGroupSmgKpis = buildMetricValuesHook({
  groupFilterType: 'corporate',
  metrics: {
    codes: ['smg_training_completion_percent'],
  },
})

const useLocationGuestAiSummary = (): IApiDataType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const brandLocationGroupId = getBrandLocationGroupId()

  const { data: guestReviewData, loading: isGuestReviewDataLoading } =
    useQuery<IGuestSummaryType>(query, {
      variables: {
        iLocationId: groupFilter?.ids?.[0],
        iStartDate: startDate,
        iEndDate: endDate,
      },
      skip: !startDate || !endDate || !groupFilter,
    })

  const { data: locationSmgData, loading: isLocationSmgDataLoading } =
    useLocationSmgKpis()
  const { data: systemwideSmgData, loading: isSystemwideSmgDataLoading } =
    useSystemwideGroupSmgKpis({
      customLocationGroupIds: [brandLocationGroupId],
      shouldBypassRowLevelSecurity: true,
    })

  return {
    data: useMemo(() => {
      const guestReviewSummary =
        guestReviewData?.locationGuestSummaryV2?.nodes?.[0]

      const summaryMessage = (() => {
        if (!guestReviewSummary) return `There are no SMG comments.`

        if (guestReviewSummary.problemPercentage) {
          return `${guestReviewSummary.problemPercentage}% of your SMG responses had a problem.`
        } else {
          return `Great job! None of your SMG responses had a problem!`
        }
      })()

      let trainingCompletionInsight = ''
      if (
        locationSmgData?.smgTrainingCompletionPercent &&
        systemwideSmgData?.smgTrainingCompletionPercent
      ) {
        trainingCompletionInsight = `Training Completion rate is ${(
          locationSmgData.smgTrainingCompletionPercent as number
        ).toFixed(2)}% which is `
        if (
          locationSmgData.smgTrainingCompletionPercent >
          systemwideSmgData.smgTrainingCompletionPercent
        ) {
          trainingCompletionInsight += `higher than system average.`
        } else if (
          locationSmgData.smgTrainingCompletionPercent <
          systemwideSmgData.smgTrainingCompletionPercent
        ) {
          trainingCompletionInsight += `lower than system average.`
        } else {
          trainingCompletionInsight += `the same as system average.`
        }
      }
      const allInsights: string[] = []

      if (guestReviewSummary) {
        allInsights.push(
          `The top problem ${
            guestReviewSummary.mostCommonProblem.length > 1 ? 'were' : 'was'
          }: ${guestReviewSummary.mostCommonProblem.join(', ')}.`,
        )
      }
      if (Array.isArray(guestReviewSummary?.mostCommonDay)) {
        allInsights.push(
          `${guestReviewSummary.mostCommonDay
            .map((day: string) => `${day}`)
            .join(', ')} have the most negative reviews.`,
        )
      }
      if (allInsights.length > 0) {
        allInsights.push('Address these issues with your team.')
      }
      if (trainingCompletionInsight) {
        allInsights.unshift(trainingCompletionInsight)
      }

      const insightMessage =
        allInsights.length > 0
          ? allInsights.join(' ')
          : 'Data is not enough to generate insights ...'

      const hasEnoughData =
        guestReviewSummary && locationSmgData && systemwideSmgData

      return {
        conclusion: hasEnoughData ? summaryMessage : 'Loading ...',
        insight: hasEnoughData ? insightMessage : 'Loading ...',
      }
    }, [
      guestReviewData,
      isGuestReviewDataLoading,
      locationSmgData,
      isLocationSmgDataLoading,
      systemwideSmgData,
      isSystemwideSmgDataLoading,
    ]),
    loading:
      isGuestReviewDataLoading ||
      isLocationSmgDataLoading ||
      isSystemwideSmgDataLoading,
  }
}

export default useLocationGuestAiSummary
